import { Collapse } from '@mui/material';
import { useState, type ReactNode } from 'react';

export interface GroupShellProps {
  /** Renders the band. Receives the open state and a toggle. */
  header: (open: boolean, toggle: () => void) => ReactNode;
  children: ReactNode;
  /** Uncontrolled starting state. Ignored when `open` is supplied. */
  defaultOpen?: boolean;
  /**
   * Controlled open state. Screens that track only explicitly-collapsed keys
   * (so pages loaded later stay open) pass `open` and own the toggle.
   */
  open?: boolean;
  onToggle?: () => void;
}

/**
 * Collapsible group: a band plus its rows. Uncontrolled by default; supply
 * `open` + `onToggle` for the inverted "everything open unless collapsed"
 * tracking the activity feed needs.
 */
export const GroupShell = ({
  header,
  children,
  defaultOpen = true,
  open,
  onToggle,
}: GroupShellProps) => {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const isControlled = open !== undefined;
  const isOpen = isControlled ? open : uncontrolledOpen;

  const toggle = () => {
    if (isControlled) {
      onToggle?.();
      return;
    }
    setUncontrolledOpen(current => !current);
  };

  return (
    <>
      {header(isOpen, toggle)}
      <Collapse in={isOpen} unmountOnExit>
        {children}
      </Collapse>
    </>
  );
};
